import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { 
    Plus, 
    Search, 
    Edit, 
    Trash2, 
    Eye,
    EyeOff,
    Calendar
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogManagement() {
    const { getAuthHeader } = useAuth();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        fetchPosts();
    }, []);

    const fetchPosts = async () => {
        try {
            const response = await axios.get(`${API}/blog/all`, getAuthHeader());
            setPosts(response.data);
        } catch (error) {
            console.error('Error fetching posts:', error);
            // Show sample data if no posts
            setPosts([]);
        } finally {
            setLoading(false);
        }
    };

    const deletePost = async (postId) => {
        if (!window.confirm('Are you sure you want to delete this post?')) return;
        
        try {
            await axios.delete(`${API}/blog/${postId}`, getAuthHeader());
            setPosts(prev => prev.filter(post => post.id !== postId));
            toast.success('Post deleted');
        } catch (error) {
            console.error('Error deleting post:', error);
            toast.error('Failed to delete post');
        }
    };

    const togglePublish = async (post) => {
        try {
            await axios.put(`${API}/blog/${post.id}`, {
                is_published: !post.is_published
            }, getAuthHeader());
            setPosts(prev => prev.map(p => 
                p.id === post.id ? { ...p, is_published: !p.is_published } : p
            ));
            toast.success(post.is_published ? 'Post unpublished' : 'Post published');
        } catch (error) {
            console.error('Error updating post:', error);
            toast.error('Failed to update post');
        }
    };

    const filteredPosts = posts.filter(post =>
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.category?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    return (
        <div data-testid="blog-management">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <h1 className="text-3xl font-bold">Blog Posts</h1>
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            type="text"
                            placeholder="Search posts..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10 w-64"
                            data-testid="blog-search"
                        />
                    </div>
                    <Button asChild className="rounded-full" data-testid="new-post-btn">
                        <Link to="/admin/blog/new">
                            <Plus className="w-4 h-4 mr-2" />
                            New Post
                        </Link>
                    </Button>
                </div>
            </div>

            {loading ? (
                <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="p-6 rounded-2xl border border-border bg-background">
                            <div className="h-6 skeleton rounded w-1/3 mb-4" />
                            <div className="h-4 skeleton rounded w-2/3" />
                        </div>
                    ))}
                </div>
            ) : filteredPosts.length > 0 ? (
                <div className="space-y-4">
                    {filteredPosts.map((post) => (
                        <div 
                            key={post.id}
                            className="p-6 rounded-2xl border border-border bg-background"
                            data-testid={`post-${post.id}`}
                        >
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                        <h3 className="text-lg font-semibold">{post.title}</h3>
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                            post.is_published 
                                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                                : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                                        }`}>
                                            {post.is_published ? 'Published' : 'Draft'}
                                        </span>
                                    </div>
                                    <p className="text-sm text-muted-foreground mb-3 line-clamp-1">
                                        {post.excerpt}
                                    </p>
                                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                        <span className="px-2 py-1 rounded bg-muted">
                                            {post.category?.replace(/-/g, ' ')}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <Calendar className="w-3 h-3" />
                                            {formatDate(post.created_at)}
                                        </span>
                                        <span>By {post.author}</span>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => togglePublish(post)}
                                        title={post.is_published ? 'Unpublish' : 'Publish'}
                                    >
                                        {post.is_published ? (
                                            <EyeOff className="w-4 h-4" />
                                        ) : (
                                            <Eye className="w-4 h-4" />
                                        )}
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        asChild
                                    >
                                        <Link to={`/admin/blog/edit/${post.id}`}>
                                            <Edit className="w-4 h-4" />
                                        </Link>
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => deletePost(post.id)}
                                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-center py-20">
                    <p className="text-lg text-muted-foreground mb-4">No blog posts yet</p>
                    <Button asChild className="rounded-full">
                        <Link to="/admin/blog/new">
                            <Plus className="w-4 h-4 mr-2" />
                            Create Your First Post
                        </Link>
                    </Button>
                </div>
            )}
        </div>
    );
}
